import React from 'react';
import { FormattedMessage } from 'react-intl';

import PaymentInstructions from 'components/Core/SidePanel/PaymentInstructions/PaymentInstructions';
import ActionModal from 'components/Shared/ActionModal';
import { shouldShowACHEnabledOnRefresh } from 'components/Shared/creditCardSurcharging/creditCardSurcharging';
import { SplunkReporter } from 'reporting/splunk/SplunkReporter';
import { companyInfoSelectors } from 'store/companyInfo/selectors';
import { saleSelectors } from 'store/sale/selectors';
import { useSelector } from 'store/utils';
const splunkReporter = SplunkReporter.getInstance();
const logger = 'components/Experiments/creditCardSurcharging/creditCardSurchargingModal.tsx';
export interface CreditCardSurchargingModalProps {
  hide: () => void;
  onContinue: () => void;
}

const CreditCardSurchargingModal: React.FC<CreditCardSurchargingModalProps> = ({
  hide,
  onContinue,
}) => {
  const merchantEmail = useSelector((state) =>
    companyInfoSelectors.emailSelector(state.companyInfo)
  );
  const paymentDetails = useSelector((state) =>
    saleSelectors.paymentDetailsMessageSelector(state.sale)
  );
  const featureFlags = useSelector((state) => state.featureFlags || {});
  const sale = useSelector((state) => state.sale);
  const showACHEnabledOnRefresh = shouldShowACHEnabledOnRefresh({ featureFlags, sale });

  const isPaymentInstruction = typeof paymentDetails === 'string';
  const headerIntl = showACHEnabledOnRefresh
    ? {
        id: 'CC_SURCHARGE_MODAL_TITLE_SHOW_ACH_AFTER_REFRESH',
        defaultMessage:
          'Credit card payments aren’t available, but you can still pay by ACH bank payment',
      }
    : {
        id: 'CC_SURCHARGE_MODAL_TITLE',
        defaultMessage: 'Online payment isn’t available for this invoice',
      };

  const continueIntl = { id: 'CLOSE_SURCHARGE_MODAL', defaultMessage: 'Close' };

  const refreshPage = (eventName: string) => {
    splunkReporter.contextual({
      logInfo: { logLevel: 'info', logger },
      event: 'view',
      action: 'render',
      activityInfo: {
        activityObject: 'Surcharge PDT',
        action: 'refresh',
        event: eventName,
        isPaymentInstruction,
        showACHEnabledOnRefresh,
        hasMerchantEmail: typeof merchantEmail === 'string',
      },
    });

    if (
      typeof window === 'object' &&
      window.location &&
      typeof window.location.reload === 'function'
    ) {
      window.location.reload();
    }
  };

  const handleContinue = () => {
    onContinue();
    refreshPage('continue');
  };
  const handleHide = () => {
    hide();
    refreshPage('hide');
  };

  return (
    <ActionModal
      hide={handleHide}
      onContinue={handleContinue}
      headerIntl={headerIntl}
      continueIntl={continueIntl}
      hideOnContinueFinish={true}
      marginTop="32px"
      withHr={true}
      withCancelBtn={false}
      customDesignKey={'ccSurchargingModal'}
      width={'100px'}
      buttonType="primary"
      buttonCustomizations={{
        logoUrl: '',
        themeColor: '',
        buttonTextColor: '',
        buttonColor: '',
        buttonBorder: '',
        buttonBorderRadius: '4px',
      }}
    >
      <div>
        {showACHEnabledOnRefresh && (
          <FormattedMessage
            id="CC_SURCHARGE_MODAL_BODY_SHOW_ACH_AFTER_REFRESH"
            defaultMessage={
              'This invoice can’t be paid with a credit card, but you can still pay quickly and securely using a bank transfer (ACH). A convenience fee may apply.'
            }
          />
        )}

        {!isPaymentInstruction && !showACHEnabledOnRefresh && (
          <FormattedMessage
            id="CC_SURCHARGE_MODAL_BODY_CONTACT"
            defaultMessage="If you aren't sure how to pay, contact the business at {email}."
            values={{ email: merchantEmail }}
          />
        )}

        {isPaymentInstruction && !showACHEnabledOnRefresh && (
          <>
            <FormattedMessage
              id="CC_SURCHARGE_MODAL_BODY_CONTACT_PAYMENT_INSTRUCTIONS"
              defaultMessage="Follow the business' payment instructions to pay this invoice. If you need help, contact the business directly."
            />
            <div style={{ marginTop: 12 }}>
              <PaymentInstructions />
            </div>
          </>
        )}
      </div>
    </ActionModal>
  );
};

export default CreditCardSurchargingModal;
